import React, { useEffect, useRef, useState } from 'react';
import './Pricing.css';
import { TopNav, SiteFooter } from './SiteChrome';
import { useLanguage } from './LanguageContext';

/* ── Checkmark SVG Icon ─────────────────────────────────────── */
const CheckIcon = () => (
  <svg
    className="pricing-check-icon"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="10" cy="10" r="9" fill="rgba(76,214,251,0.12)" />
    <path
      d="M6 10.5l2.8 2.8 5.2-5.6"
      stroke="#4cd6fb"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* ── WhatsApp arrow icon ─────────────────────────────────────── */
const ArrowIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ── Single Pricing Card ─────────────────────────────────────── */
function PricingCard({ plan, ctaLabel, saveBadge, popularBadge, waGreeting, waNumber, index }) {
  const wrapperRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.08 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const waMessage = encodeURIComponent(`${waGreeting} ${plan.name}.`);
  const waLink = `https://wa.me/${waNumber}?text=${waMessage}`;

  const wrapperStyle = {
    opacity: visible ? 1 : 0,
    transform: visible
      ? hovered ? 'translateY(-8px)' : 'translateY(0)'
      : 'translateY(36px)',
    transition: `opacity 0.6s cubic-bezier(0.2,0.8,0.2,1) ${index * 120}ms,
                 transform 0.5s cubic-bezier(0.2,0.8,0.2,1) ${index * 120}ms`,
  };

  return (
    /* ── wrapper: reserving space above for the popular badge ── */
    <div
      ref={wrapperRef}
      className={`pricing-card-wrapper${plan.isPopular ? ' pricing-card-wrapper--popular' : ''}`}
      style={wrapperStyle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* "Paling Populer" badge sits ABOVE the card */}
      {plan.isPopular && (
        <div className="pricing-popular-banner" aria-label={popularBadge}>
          <span className="pricing-popular-dot" aria-hidden="true" />
          {popularBadge}
        </div>
      )}

      {/* ── the actual card ── */}
      <div className={`pricing-card${plan.isPopular ? ' pricing-card--popular' : ''}${plan.isCustom ? ' pricing-card--custom' : ''}`}>

        {/* Discount badge — top-right corner of card */}
        {plan.discount && (
          <span className="pricing-discount-badge">{plan.discount}</span>
        )}

        {/* Card header */}
        <div className="pricing-card-header">
          <h3 className="pricing-plan-name">{plan.name}</h3>
          <p className="pricing-plan-tagline">{plan.tagline}</p>
        </div>

        {/* Price block */}
        <div className="pricing-price-block">
          {/* "Save Money" badge */}
          {!plan.isCustom && (
            <div className="pricing-save-badge" aria-label={saveBadge}>
              <span className="pricing-save-dot" aria-hidden="true" />
              {saveBadge}
            </div>
          )}

          {plan.originalPrice && (
            <p className="pricing-original-price">
              <s>{plan.originalPrice}</s>
            </p>
          )}

          <p className={`pricing-main-price${plan.isCustom ? ' pricing-main-price--custom' : ''}`}>
            {plan.isCustom ? (
              <span className="pricing-custom-label">{plan.price}</span>
            ) : (
              plan.price
            )}
          </p>
        </div>

        {/* CTA Button */}
        <a
          href={waLink}
          target="_blank"
          rel="noopener noreferrer"
          className={`pricing-cta-btn${plan.isPopular ? ' pricing-cta-btn--popular' : ''}`}
          aria-label={`${ctaLabel} — ${plan.name}`}
        >
          {ctaLabel}
          <ArrowIcon />
        </a>

        {/* Divider */}
        <div className="pricing-divider" aria-hidden="true" />

        {/* Feature list */}
        <ul className="pricing-features" aria-label={`Fitur ${plan.name}`}>
          {plan.features.map((feat) => (
            <li key={feat} className="pricing-feature-item">
              <CheckIcon />
              <span>{feat}</span>
            </li>
          ))}
        </ul>

        {/* Glow orb for popular card */}
        {plan.isPopular && <div className="pricing-card-glow" aria-hidden="true" />}
      </div>
    </div>
  );
}

/* ── Main Pricing Page ───────────────────────────────────────── */
const Pricing = () => {
  const { content } = useLanguage();
  const t = content.team;

  const heroRef = useRef(null);
  const [heroVisible, setHeroVisible] = useState(false);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setHeroVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="pricing-page">
      <TopNav />

      <main className="pricing-main">
        {/* Ambient BG */}
        <div className="pricing-particle-bg" aria-hidden="true">
          <div className="pricing-orb pricing-orb--1" />
          <div className="pricing-orb pricing-orb--2" />
          <div className="pricing-orb pricing-orb--3" />
        </div>

        {/* Hero */}
        <section
          ref={heroRef}
          className="pricing-hero"
          style={{
            opacity: heroVisible ? 1 : 0,
            transform: heroVisible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.9s cubic-bezier(0.2,0.8,0.2,1), transform 0.9s cubic-bezier(0.2,0.8,0.2,1)',
          }}
        >
          <span className="pricing-eyebrow">{t.eyebrow}</span>
          <h1 className="pricing-title">
            {t.title}{' '}
            <span className="pricing-title-highlight">{t.titleHighlight}</span>
          </h1>
          <p className="pricing-subtitle">{t.subtitle}</p>
        </section>

        {/* Cards Grid */}
        <section className="pricing-grid-section" aria-label="Pilihan paket harga">
          <div className="pricing-grid">
            {t.plans.map((plan, i) => (
              <PricingCard
                key={plan.id}
                plan={plan}
                index={i}
                ctaLabel={t.ctaLabel}
                saveBadge={t.saveBadge}
                popularBadge={t.popularBadge}
                waGreeting={t.waGreeting}
                waNumber={t.waNumber}
              />
            ))}
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
};

export default Pricing;
